// A shared After Hours episode, reached at /afterhours/<slug>/<token> with
// no login. The token is the only credential. This page is the seed of how
// the show will be served publicly: cover, title, description, a player
// that names itself on the lock screen, the transcript, and the feed link.
import { useEffect, useRef, useState } from "react";
import { API_BASE } from "../api";

type Episode = {
  number: number; show_day: string; title: string; description: string; artist: string; album: string;
  duration_s: number | null; audio_url: string; cover_url: string; share_url: string; feed_url: string;
  transcript: string;
};

export default function PublicPodcast({ token }: { token: string }) {
  const [ep, setEp] = useState<Episode | null>(null);
  const [gone, setGone] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const player = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/public/podcast/${encodeURIComponent(token)}`);
        if (!r.ok) { setGone(true); return; }
        setEp(await r.json());
      } catch { setGone(true); }
    })();
  }, [token]);

  useEffect(() => {
    if (!ep || !("mediaSession" in navigator)) return;
    const ms = navigator.mediaSession;
    // iOS forgets metadata set before playback starts, so it is set again on
    // every play/pause, and the playback state is kept in step.
    const name = () => {
      ms.metadata = new MediaMetadata({
        title: `#${ep.number} · ${ep.title}`, artist: ep.artist, album: ep.album,
        artwork: [{ src: `${ep.cover_url}?size=600`, sizes: "600x600", type: "image/jpeg" },
                  { src: ep.cover_url, sizes: "1400x1400", type: "image/jpeg" }],
      });
    };
    name();
    const a = player.current;
    const pos = () => {
      const x = player.current;
      if (!x || !isFinite(x.duration)) return;
      try { ms.setPositionState({ duration: x.duration, playbackRate: x.playbackRate || 1, position: x.currentTime }); } catch { /* */ }
    };
    const onPlay = () => { name(); pos(); try { ms.playbackState = "playing"; } catch { /* */ } };
    const onPause = () => { try { ms.playbackState = "paused"; } catch { /* */ } };
    for (const ev of ["play", "playing", "loadedmetadata", "durationchange"]) a?.addEventListener(ev, onPlay);
    a?.addEventListener("timeupdate", pos); a?.addEventListener("seeked", pos);
    a?.addEventListener("pause", onPause);
    const el = () => player.current;
    const handlers: [MediaSessionAction, MediaSessionActionHandler][] = [
      ["play", () => { void el()?.play(); }],
      ["pause", () => el()?.pause()],
      ["seekbackward", (d) => { const a = el(); if (a) a.currentTime = Math.max(0, a.currentTime - (d.seekOffset ?? 10)); }],
      ["seekforward", (d) => { const a = el(); if (a) a.currentTime = Math.min(a.duration || Infinity, a.currentTime + (d.seekOffset ?? 10)); }],
      ["seekto", (d) => { const a = el(); if (a && d.seekTime != null) a.currentTime = d.seekTime; }],
    ];
    for (const [action, fn] of handlers) { try { ms.setActionHandler(action, fn); } catch { /* unsupported */ } }
    return () => {
      for (const ev of ["play", "playing", "loadedmetadata", "durationchange"]) a?.removeEventListener(ev, onPlay);
      a?.removeEventListener("timeupdate", pos); a?.removeEventListener("seeked", pos);
      a?.removeEventListener("pause", onPause);
      for (const [action] of handlers) { try { ms.setActionHandler(action, null); } catch { /* */ } }
    };
  }, [ep]);

  const mins = ep?.duration_s != null ? `${Math.floor(ep.duration_s / 60)}:${String(ep.duration_s % 60).padStart(2, "0")}` : null;
  const [copied, setCopied] = useState(false);
  // The link to pass on is the one that previews as THIS episode — never
  // this page's own address, whose preview is the generic site card.
  const copyShare = async () => {
    if (!ep) return;
    try { await navigator.clipboard.writeText(ep.share_url); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { window.prompt("Copy this link:", ep.share_url); }
  };
  useEffect(() => { if (ep) document.title = `#${ep.number} · ${ep.title} — The Bull Sheet After Hours`; }, [ep]);

  return (
    <div className="min-h-screen bg-ink text-slate-100">
      <header className="border-b border-edge">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <span className="font-black tracking-tight text-lg uppercase">Stonky</span>
          <span className="text-slate-500 text-sm">The Bull Sheet After Hours</span>
          {ep && <a className="ml-auto text-xs text-gold hover:underline" href={ep.feed_url}>RSS feed</a>}
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8">
        {gone && (
          <div className="card text-center">
            <div className="text-lg font-semibold">This episode isn't available.</div>
            <div className="text-slate-500 text-sm mt-1">The link may have been taken down.</div>
          </div>
        )}
        {!ep && !gone && <div className="text-slate-500">loading…</div>}
        {ep && (
          <article className="space-y-5">
            <div className="flex gap-5 items-start flex-wrap">
              <img src={`${ep.cover_url}?size=600`} alt="After Hours" className="w-40 h-40 rounded-xl object-cover shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-xs font-mono text-slate-500">Episode {ep.number} · {ep.show_day}{mins ? ` · ${mins}` : ""}</div>
                <h1 className="text-2xl font-bold mt-1">{ep.title}</h1>
                <div className="text-sm text-slate-400 mt-1">{ep.artist}</div>
                <p className="text-sm text-slate-300 mt-3">{ep.description}</p>
              </div>
            </div>
            <audio ref={player} controls playsInline src={ep.audio_url} className="w-full" preload="metadata" title={`#${ep.number} · ${ep.title}`} />
            <div className="flex items-center gap-2 flex-wrap">
              <button className="btn" onClick={copyShare}>{copied ? "Link copied ✓" : "Share this episode"}</button>
              <span className="text-xs text-slate-500">The link names the episode wherever it's pasted.</span>
            </div>
            <div className="text-xs text-slate-500">Grounded by rule. Every number on the show is cited; every call is logged and graded. Not investment advice.</div>
            <div>
              <button className="btn" onClick={() => setShowTranscript((v) => !v)}>{showTranscript ? "Hide transcript" : "Transcript"}</button>
              {showTranscript && <pre className="mt-3 whitespace-pre-wrap text-sm text-slate-300 font-sans">{ep.transcript}</pre>}
            </div>
          </article>
        )}
      </main>
    </div>
  );
}
